import type { TDesignSystem } from "@/shared/types";
import { useDesignSystemStore } from "../../../store/design-system.store";
import ThemeItemActions from "./theme-item-actions";

const COLOR_KEYS = [
  "primaryColor",
  "secondaryColor",
  "themeColor3",
  "themeColor4",
  "backgroundColor",
] as const;

const ThemeListItem = ({
  theme,
  isDefault,
  close,
  onSelectTheme,
}: {
  theme: TDesignSystem;
  isDefault: boolean;
  close: () => void;
  // When provided, selecting a theme reports its id to the caller instead of
  // applying it to the editor's design-system store. Lets non-editor surfaces
  // (e.g. File-to-Storydoc) reuse this list purely to pick a theme.
  onSelectTheme?: (themeId: string) => void;
}) => {
  const { designSystem, title } = theme;
  const update = useDesignSystemStore((s) => s.update);
  const updateCustomColors = useDesignSystemStore((s) => s.updateCustomColors);

  const headlineFont = extractFontName(designSystem.titleFont as string);
  const bodyFont = extractFontName(designSystem.paragraphFont as string);
  const fontLabel =
    headlineFont && bodyFont
      ? `${headlineFont} / ${bodyFont}`
      : headlineFont || bodyFont;

  const handleApply = () => {
    if (onSelectTheme) {
      onSelectTheme(theme._id);
      close();
      return;
    }
    update(designSystem as Record<string, unknown>);
    // Pull in the theme's custom-color palette so it travels with the rest of
    // the design system (Monday #12081653376). Only when the theme carries
    // colors, so importing a legacy theme doesn't wipe the staged palette.
    if (theme.customColors) updateCustomColors(theme.customColors);
    close();
  };

  return (
    <div
      onClick={handleApply}
      className="tw-[.theme-select-flyout_&]:tw-border-b-0 tw-group tw-cursor-pointer tw-border-b tw-border-solid tw-border-blue-100/50 tw-py-4 tw-transition-colors tw-duration-200 last:tw-border-b-0"
    >
      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-1">
        <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
          {title}
        </span>
        {isDefault && (
          <span className="tw-border-md tw-shrink-0 tw-rounded tw-bg-violet-100 tw-px-2 tw-py-1 tw-font-sans tw-text-xs tw-font-medium tw-text-violet-700">
            Default
          </span>
        )}
        <div className="tw-shrink-0" onClick={(e) => e.stopPropagation()}>
          <ThemeItemActions
            theme={theme}
            isDefault={isDefault}
            closeThemesSelectPopover={close}
          />
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-p-3 tw-transition-colors tw-duration-200 hover:tw-bg-blue-50/50">
        <div className="tw-flex tw-items-center tw-gap-2">
          {COLOR_KEYS.map((key) => (
            <span
              key={key}
              className="tw-block tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-blue/10"
              style={{ background: designSystem[key] as string }}
            />
          ))}
        </div>
        {fontLabel && (
          <span className="tw-block tw-font-sans tw-text-xs tw-font-normal tw-text-blue-700">
            {fontLabel}
          </span>
        )}
      </div>
    </div>
  );
};

const extractFontName = (value?: string) => {
  if (!value) return "";
  const trimmed = value.trim();
  const unquoted = trimmed.replace(/^["']|["']$/g, "");
  const firstFont = unquoted.split(",")[0].trim();
  return firstFont;
};

export default ThemeListItem;
